import {ColorOption} from '../models/appearance';

/**
 * User-selectable tile colors available in the color picker and CSV imports.
 * These match the color options shown in the tile editor UI.
 *
 * Note: The complete ColorOption type is defined in shared/models/appearance.ts
 * and includes system colors (yellow, red) used for alarm conditions.
 */
export const USER_SELECTABLE_COLORS: readonly ColorOption[] = [
  '',           // Default/No color
  'alt',        // Alternate base
  'green',      // Green base
  'indigo',     // Indigo base
  'alt-white',
  'indigo-white',
  'green-white',
  'black-white',
  'indigo-black',
  'green-black',
] as const;

/**
 * System colors set programmatically by the application (e.g., for alarm conditions).
 * Not available in color picker but are valid ColorOption values.
 */
export const SYSTEM_COLORS: readonly ColorOption[] = [
  'yellow',  // Warning alarm conditions
  'red'      // Critical alarm conditions
] as const;

/**
 * All valid tile colors as a Set for efficient validation.
 * Combines user-selectable and system colors to match the complete ColorOption type.
 */
export const VALID_TILE_COLORS: ReadonlySet<ColorOption> = new Set<ColorOption>([
  ...USER_SELECTABLE_COLORS,
  ...SYSTEM_COLORS
]);

/**
 * Validates if a color string is a valid ColorOption.
 * Invalid colors are treated as empty string to prevent CSS issues.
 *
 * @param color - The color value to validate
 * @returns The color if valid, otherwise empty string
 *
 * @example
 * isValidTileColor('green')    // Returns: 'green'
 * isValidTileColor('invalid')  // Returns: ''
 * isValidTileColor('yellow')   // Returns: 'yellow' (system color)
 */
export function isValidTileColor(color: string | ColorOption): ColorOption {
  return VALID_TILE_COLORS.has(color as ColorOption) ? (color as ColorOption) : '';
}
